// Lastslate Fountain ↔ ProseMirror bridge.
//
// Two pure functions span the boundary between the on-disk format
// (Fountain) and the in-memory ProseMirror document.
//
//   fountainToDoc(parsedScript): Node
//     Input: the existing fountain-js parse output (the same object
//     loadScript today assigns to state.script). Returns a single
//     ProseMirror doc whose top-level children are the screenplay
//     blocks in document order. Title page and title are stored as
//     attrs on the doc node so they survive the round-trip.
//
//   docToFountain(doc): string
//     Inverse. Walks doc.content and emits the same Fountain text
//     serializeFountain (renderer/js/app.js) produces for the same
//     state.script — title-page block first, then body. Force-syntax
//     (the `forced` attr) emits the prefix on output:
//       scene_heading → `.SLUG`
//       action        → `!ACTION`
//       character     → `@CHARACTER`
//       transition    → `>TRANSITION`
//     Note: fountain-js does NOT preserve force-syntax on parse, so a
//     fresh fountainToDoc never marks `forced: true`. Force-syntax
//     round-trip only matters once writers toggle it through the PM
//     commands layer (Phase 3+).
//
// Both functions are pure: no DOM, no state.* access.

import { schema } from './schema.js';
import { stampDoc } from './block-ids.js';

// Keep in sync with CAPS_STORAGE_TYPES in renderer/js/app.js. These
// block types are uppercase-normalized on disk.
const CAPS_STORAGE_TYPES = new Set(['scene-heading', 'character', 'transition', 'shot']);

// Title-page key emission order. Matches serializeFountain's TP_ORDER
// so the round-trip test produces byte-identical title pages.
const TP_ORDER = ['title', 'credit', 'author', 'source', 'draft date', 'contact', 'notes'];

// Force-syntax prefix per block type. `centered` has its own `> … <`
// wrap, handled separately below.
const FORCE_PREFIX = {
  scene_heading: '.',
  action: '!',
  character: '@',
  transition: '>',
};

// Storage block-type (kebab-case, the vocabulary used by state.script
// and the on-disk DOM) ↔ schema node name (underscore, JS-safe key).
const STORAGE_TO_NODE = {
  'scene-heading': 'scene_heading',
  action: 'action',
  character: 'character',
  parenthetical: 'parenthetical',
  dialogue: 'dialogue',
  transition: 'transition',
  shot: 'shot',
  centered: 'centered',
};

const NODE_TO_STORAGE = Object.fromEntries(
  Object.entries(STORAGE_TO_NODE).map(([storage, node]) => [node, storage]),
);

function titleCaseKey(key) {
  return String(key).replace(/\b\w/g, (c) => c.toUpperCase());
}

function makeNode(storageType, text, extraAttrs = {}) {
  const nodeName = STORAGE_TO_NODE[storageType];
  if (!nodeName) {
    throw new Error(`fountain-bridge: unknown block type "${storageType}"`);
  }
  const nodeType = schema.nodes[nodeName];
  const attrs = {};
  for (const key of Object.keys(nodeType.spec.attrs || {})) {
    if (key in extraAttrs) attrs[key] = extraAttrs[key];
  }
  const content = text ? [schema.text(text)] : [];
  return nodeType.create(attrs, content);
}

export function fountainToDoc(parsedScript) {
  if (!parsedScript || typeof parsedScript !== 'object') {
    throw new TypeError('fountainToDoc: expected parsed script object');
  }
  const blocks = [];
  const scenes = Array.isArray(parsedScript.scenes) ? parsedScript.scenes : [];
  const prelude = Array.isArray(parsedScript.prelude) ? parsedScript.prelude : [];

  // Prelude blocks (FADE IN:, opening action) render before any scene
  // heading. They are NOT navigable scenes in the sidebar — see
  // shared.js for the RawParseResult contract.
  const emitBlock = (b) => {
    if (!b || !b.type) return;
    if (!STORAGE_TO_NODE[b.type]) {
      blocks.push(makeNode('action', String(b.text || '')));
      return;
    }
    blocks.push(makeNode(b.type, String(b.text || '')));
  };

  for (const b of prelude) emitBlock(b);

  for (const scene of scenes) {
    if (scene.heading) {
      blocks.push(makeNode('scene-heading', scene.heading));
    }
    const sceneBlocks = Array.isArray(scene.blocks) ? scene.blocks : [];
    for (const b of sceneBlocks) emitBlock(b);
  }

  // Empty docs are illegal under the schema (content: 'block+'). Seed
  // with an empty action so the editor mounts cleanly on a blank script.
  if (blocks.length === 0) {
    blocks.push(makeNode('action', ''));
  }

  const titlePage =
    parsedScript.titlePage && typeof parsedScript.titlePage === 'object'
      ? { ...parsedScript.titlePage }
      : null;
  const title = parsedScript.title || (titlePage && titlePage.title) || null;

  // Stamp every block with a UUIDv7 `bid` before handing the doc to
  // the editor. Fountain has no native ID slot, so we don't try to
  // preserve bids across .fountain → on-disk → .fountain round-trips —
  // the local sqlite Yjs cache holds the authoritative IDs for any
  // project that's been opened before. A raw Fountain import (no
  // sqlite cache yet) just gets fresh IDs; nothing references the old
  // ones in that case anyway.
  return stampDoc(schema.nodes.doc.create({ title, titlePage }, blocks));
}

function emitTitlePage(out, titlePage, fallbackTitle) {
  if (!titlePage && !fallbackTitle) return false;
  const tp = titlePage ? { ...titlePage } : {};
  if (fallbackTitle && !tp.title) tp.title = fallbackTitle;
  const emitted = new Set();
  let wrote = false;
  for (const key of TP_ORDER) {
    if (tp[key]) {
      out.push(`${titleCaseKey(key)}: ${tp[key]}`);
      emitted.add(key);
      wrote = true;
    }
  }
  for (const key of Object.keys(tp)) {
    if (!emitted.has(key) && tp[key]) {
      out.push(`${titleCaseKey(key)}: ${tp[key]}`);
      wrote = true;
    }
  }
  if (wrote) out.push('');
  return wrote;
}

function emitBlockLine(node) {
  const storageType = NODE_TO_STORAGE[node.type.name] || 'action';
  const raw = node.textContent || '';
  const text = CAPS_STORAGE_TYPES.has(storageType) ? raw.toUpperCase() : raw;
  const forced = node.attrs && node.attrs.forced === true;

  if (storageType === 'parenthetical') {
    const inner = text.replace(/^\(|\)$/g, '');
    return `(${inner})`;
  }
  if (storageType === 'centered') {
    // Centered blocks emit `> text <`. serializeFountain doesn't
    // currently re-emit the wrap markers; preserving them here lets
    // a round-trip survive once the legacy serializer catches up.
    return `> ${text} <`;
  }
  if (forced && FORCE_PREFIX[node.type.name]) {
    return `${FORCE_PREFIX[node.type.name]}${text}`;
  }
  return text;
}

function isClusterRun(curStorage, nextStorage) {
  return (
    (curStorage === 'character' || curStorage === 'parenthetical' || curStorage === 'dialogue') &&
    (nextStorage === 'parenthetical' || nextStorage === 'dialogue')
  );
}

export function docToFountain(doc) {
  if (!doc || typeof doc.forEach !== 'function') {
    throw new TypeError('docToFountain: expected a ProseMirror doc node');
  }
  const out = [];
  emitTitlePage(out, doc.attrs && doc.attrs.titlePage, doc.attrs && doc.attrs.title);

  const blocks = [];
  doc.forEach((node) => blocks.push(node));

  for (let i = 0; i < blocks.length; i++) {
    const node = blocks[i];
    const next = blocks[i + 1];
    const curStorage = NODE_TO_STORAGE[node.type.name] || 'action';
    const nextStorage = next ? NODE_TO_STORAGE[next.type.name] || 'action' : null;

    // Scene heading is its own block but serializeFountain emits
    // scene.heading separately from scene.blocks with a forced trailing
    // blank line. We mirror that here: always blank line after a
    // scene_heading, never collapse into a cluster.
    out.push(emitBlockLine(node));
    if (curStorage === 'scene-heading') {
      out.push('');
      continue;
    }
    if (!isClusterRun(curStorage, nextStorage)) {
      out.push('');
    }
  }

  return out.join('\n');
}
